<script setup>
// Reusable "Assign email to a chat" modal — links a Fangate buyer email to a
// Telegram contact (FangateChatLink) so its sales surface under the panel's
// "This Chat" tab. Used by the in-chat panel AND the Account-performance
// transactions list. Parent opens it via a template ref: `modalRef.open(email)`.
import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import FangateAPI from 'dashboard/api/fangate';
import ConversationApi from 'dashboard/api/inbox/conversation';

const props = defineProps({
  inboxId: { type: [Number, String], default: null },
});
const emit = defineEmits(['assigned']);
const store = useStore();

const state = reactive({ open: false, email: '', search: '', convId: null, busy: false, done: false, error: '', fetched: [], loading: false });

async function open(email) {
  Object.assign(state, { open: true, email: email || '', search: '', convId: null, busy: false, done: false, error: '', fetched: [], loading: true });
  // Pull this inbox's chats directly so the picker also works from Reports,
  // where the conversation store may hold a different inbox (or nothing yet).
  try {
    const res = await ConversationApi.get({ inboxId: Number(props.inboxId), status: 'all', assigneeType: 'all', page: 1 });
    state.fetched = res?.data?.data?.payload || [];
  } catch (_) { /* fall back to whatever's already in the store */ }
  state.loading = false;
}
function close() { state.open = false; }
defineExpose({ open });

const chats = computed(() => {
  const inbox = Number(props.inboxId);
  const q = state.search.trim().toLowerCase();
  // Merge freshly-fetched chats (reliable in Reports) with the store (instant
  // when already in-chat), de-duplicated by conversation id.
  const fromStore = (store.getters.getAllConversations || []).filter(c => Number(c.inbox_id) === inbox);
  const seen = new Set();
  return [...state.fetched, ...fromStore]
    .filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; })
    .map(c => ({ id: c.id, name: c.meta?.sender?.name || ('Chat #' + c.id), avatar: c.meta?.sender?.thumbnail || '' }))
    .filter(c => !q || c.name.toLowerCase().includes(q))
    .slice(0, 60);
});

async function doAssign() {
  if (!state.convId || state.busy) return;
  state.busy = true; state.error = '';
  try {
    await FangateAPI.assignChatLink(props.inboxId, { buyerEmail: state.email, conversationId: state.convId });
    state.done = true;
    useAlert('Email assigned to this chat — its sales now show under “This Chat”.');
    emit('assigned', { email: state.email, conversationId: state.convId });
    setTimeout(() => { state.open = false; }, 1000);
  } catch (_) {
    state.error = 'Could not assign — please try again.';
  }
  state.busy = false;
}
</script>

<template>
  <div v-if="state.open" class="fac-backdrop" @click="close">
    <div class="fac-modal" @click.stop>
      <div class="fac-head">
        <span class="fac-title">Assign email to a chat</span>
        <button class="fac-x" type="button" aria-label="Close" @click="close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <p class="fac-hint">
        Link this buyer’s email to a Telegram chat. From then on every sale with this email is
        also tracked under that chat’s <strong>“This Chat”</strong> tab — for individual per-chat sales stats.
      </p>
      <label class="fac-lbl">Buyer email</label>
      <div class="fac-email">{{ state.email }}</div>
      <label class="fac-lbl">Select Chat</label>
      <input v-model="state.search" class="fac-search" type="text" placeholder="Search chats of this account…" />
      <div class="fac-chats">
        <div v-if="state.loading && !chats.length" class="fac-chats-empty">Loading chats…</div>
        <div v-else-if="!chats.length" class="fac-chats-empty">No chats found.</div>
        <button
          v-for="c in chats" :key="c.id" type="button"
          class="fac-chat" :class="{ sel: state.convId === c.id }"
          @click="state.convId = c.id"
        >
          <img v-if="c.avatar" :src="c.avatar" class="fac-chat-av" alt="" />
          <span v-else class="fac-chat-av fac-chat-av--ph">{{ (c.name || '?').slice(0, 1).toUpperCase() }}</span>
          <span class="fac-chat-name">{{ c.name }}</span>
          <svg v-if="state.convId === c.id" class="fac-chat-tick" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        </button>
      </div>
      <div v-if="state.error" class="fac-err">{{ state.error }}</div>
      <div class="fac-foot">
        <button class="fac-cancel" type="button" @click="close">Cancel</button>
        <button class="fac-assign" type="button" :disabled="!state.convId || state.busy" @click="doAssign">
          {{ state.done ? '✓ Assigned' : (state.busy ? 'Assigning…' : 'Assign') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fac-backdrop { position: fixed; inset: 0; z-index: 70; background: rgba(20, 24, 28, 0.42); display: flex; align-items: center; justify-content: center; padding: 16px; }
.fac-modal { width: 100%; max-width: 380px; max-height: 86vh; overflow-y: auto; background: var(--bg-panel); border-radius: 14px; box-shadow: 0 18px 48px rgba(20, 24, 28, 0.3); padding: 16px 16px 14px; }
.fac-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.fac-title { font: 700 15px 'Libre Baskerville', serif; color: var(--ink-strong); }
.fac-x { border: none; background: none; color: var(--ink-muted); cursor: pointer; padding: 2px; line-height: 0; border-radius: 6px; }
.fac-x:hover { background: var(--bg-subtle); color: var(--ink-strong); }
.fac-hint { font: 400 12px/1.5 'Inter', sans-serif; color: var(--ink-medium); margin: 0 0 12px; }
.fac-lbl { display: block; font: 600 10.5px 'Inter', sans-serif; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-muted); margin: 10px 0 4px; }
.fac-email { font: 600 13px 'Inter', sans-serif; color: var(--ink-strong); background: var(--bg-subtle); border-radius: 8px; padding: 8px 10px; word-break: break-all; }
.fac-search { width: 100%; box-sizing: border-box; border: 1px solid var(--border-soft); border-radius: 8px; padding: 8px 10px; font: 400 13px 'Inter', sans-serif; color: var(--ink-strong); }
.fac-search:focus { outline: none; border-color: var(--accent); }
.fac-chats { margin-top: 6px; max-height: 220px; overflow-y: auto; border: 1px solid var(--border-soft); border-radius: 8px; }
.fac-chats-empty { padding: 14px; text-align: center; color: var(--ink-muted); font-size: 12px; }
.fac-chat { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; border: none; background: none; padding: 8px 10px; cursor: pointer; border-bottom: 1px solid var(--border-soft); }
.fac-chat:last-child { border-bottom: none; }
.fac-chat:hover { background: var(--bg-subtle); }
.fac-chat.sel { background: rgba(13, 148, 136, 0.10); }
.fac-chat-av { width: 26px; height: 26px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.fac-chat-av--ph { display: inline-flex; align-items: center; justify-content: center; background: var(--accent); color: #fff; font: 700 11px 'Inter', sans-serif; }
.fac-chat-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font: 500 13px 'Inter', sans-serif; color: var(--ink-strong); }
.fac-chat-tick { color: var(--accent-strong); flex-shrink: 0; }
.fac-err { color: #b3261e; font-size: 12px; margin-top: 8px; }
.fac-foot { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
.fac-cancel { border: 1px solid var(--border-soft); background: var(--bg-panel); color: var(--ink-medium); border-radius: 8px; padding: 7px 14px; font: 500 12.5px 'Inter', sans-serif; cursor: pointer; }
.fac-assign { border: none; background: var(--accent); color: #fff; border-radius: 8px; padding: 7px 16px; font: 600 12.5px 'Inter', sans-serif; cursor: pointer; }
.fac-assign:disabled { opacity: 0.5; cursor: default; }
</style>
